import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useSearchParams } from "react-router";
import {
  Avatar,
  Button,
  EmptyState,
  Icon,
  IconButton,
  Menu,
  Modal,
  PageHeader,
  Pill,
  Skeleton,
  SkeletonText,
  Textarea,
  useToast,
} from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { useRealtimeFrames } from "../lib/realtime-context.js";
import { readUploadResponse, uploadFile } from "../lib/transport.js";
import { uploadFailure } from "../lib/upload-error.js";
import { useWorkspace } from "../lib/workspace.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { LinkedText } from "../components/LinkedText.js";
import { AckListModal, PostImage, TeamPicker } from "./feed-parts.js";
import type { PostImageRef, PostTeam, TeamOption } from "./feed-parts.js";
import "./feed.css";

/* ─────────────────────────────── local types ─────────────────────────────── */

/**
 * `posts` has no title column in the schema (body, kind, audience, pinnedAt
 * only) — this is a status-update feed, not a blog. The composer is
 * body-first accordingly; there is nowhere to put a headline yet.
 */
type Reaction = { emoji: string; count: number; reacted: boolean };

type Post = {
  id: string;
  authorId: string;
  authorName: string;
  authorAvatarUrl: string | null;
  body: string;
  kind: string;
  pinnedAt: string | null;
  createdAt: string;
  commentCount: number;
  reactions: Reaction[];
  version?: number;
  editedAt?: string | null;
  /** Empty means the whole office. */
  teams?: PostTeam[];
  images?: PostImageRef[];
  /** An announcement the office must acknowledge. */
  requiresAck?: boolean;
  acknowledged?: boolean;
  /** Progress, only sent to the author and admins. */
  ackCount?: number | null;
  audienceCount?: number | null;
};

type PostInput = { body: string; kind: "update" | "announcement"; teamIds: string[]; imageIds: string[] };

type PostComment = {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  authorAvatarUrl: string | null;
  createdAt: string;
};

/** A post or comment optimistically appended but not yet confirmed by the server. */
type Pending<T> = T & { pending?: boolean };

const QUICK_REACTIONS = ["👍", "🎉", "❤️"];

function newClientId(): string {
  return typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `cid-${Date.now()}-${Math.random()}`;
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) return "just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* ──────────────────────────────────── composer ──────────────────────────────────── */

interface ComposerProps {
  onPost: (input: PostInput) => void;
  posting: boolean;
  teams: TeamOption[];
  canAnnounce: boolean;
}

const MAX_IMAGES = 4;

function Composer({ onPost, posting, teams, canAnnounce }: ComposerProps) {
  const { toast } = useToast();
  const [value, setValue] = useState("");
  const [announce, setAnnounce] = useState(false);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [images, setImages] = useState<PostImageRef[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || uploading) return;
    onPost({
      body: trimmed,
      kind: announce ? "announcement" : "update",
      teamIds,
      imageIds: images.map((i) => i.id),
    });
    setValue("");
    setAnnounce(false);
    setTeamIds([]);
    setImages([]);
  }, [value, uploading, announce, teamIds, images, onPost]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl+Enter submits — a plain Enter stays a newline here, since a
    // company-wide post is a more deliberate action than a chat message.
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  };

  const addImage = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast("Only images can be attached to a post.", { tone: "danger" });
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    uploadFile("/files", form)
      .then(readUploadResponse)
      .then((result) => {
        if (!result.ok) throw uploadFailure(result);
        const uploaded = (result.body as { file: { id: string; fileName: string; contentType: string } }).file;
        setImages((prev) => [...prev, { id: uploaded.id, fileName: uploaded.fileName, contentType: uploaded.contentType }]);
      })
      .catch((error: unknown) => {
        toast(error instanceof Error ? error.message : "Could not upload that image.", { tone: "danger" });
      })
      .finally(() => {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      });
  };

  return (
    <div className="feed-composer">
      <Textarea
        aria-label="Share an update"
        placeholder={announce ? "Write an announcement…" : "Share an update with the company…"}
        value={value}
        maxRows={10}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="feed-composer__field"
      />
      {images.length > 0 && (
        <div className="feed-composer__images">
          {images.map((image) => (
            <div key={image.id} className="feed-composer__image">
              <PostImage image={image} />
              <IconButton
                icon={<Icon name="close" size={12} />}
                label={`Remove ${image.fileName}`}
                variant="solid"
                onClick={() => setImages((prev) => prev.filter((i) => i.id !== image.id))}
              />
            </div>
          ))}
        </div>
      )}
      <TeamPicker teams={teams} value={teamIds} onChange={setTeamIds} />
      <div className="feed-composer__bar">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          hidden
          aria-label="Attach an image"
          onChange={(e) => addImage(e.target.files?.[0])}
        />
        <Button
          variant="ghost"
          size="sm"
          icon={<Icon name="document" size={14} />}
          loading={uploading}
          disabled={images.length >= MAX_IMAGES}
          onClick={() => fileRef.current?.click()}
        >
          Image
        </Button>
        {canAnnounce && (
          <label className="feed-composer__announce">
            <input type="checkbox" checked={announce} onChange={(e) => setAnnounce(e.target.checked)} />
            Announcement (needs acknowledging)
          </label>
        )}
        <div className="feed-composer__spacer" />
        <span className="feed-composer__hint">⌘/Ctrl + Enter to post</span>
        <Button icon={<Icon name="send" size={14} />} onClick={submit} loading={posting} disabled={!value.trim() || uploading}>
          Post
        </Button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────── comment thread ──────────────────────────────────── */

interface CommentThreadProps {
  postId: string;
  comments: Pending<PostComment>[] | undefined;
  loading: boolean;
  onSend: (postId: string, body: string) => void;
}

function CommentThread({ postId, comments, loading, onSend }: CommentThreadProps) {
  const [value, setValue] = useState("");

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSend(postId, trimmed);
    setValue("");
  }, [value, postId, onSend]);

  return (
    <div className="feed-comments">
      {loading ? (
        <SkeletonText lines={2} />
      ) : (
        comments?.map((comment) => (
          <div key={comment.id} className={`feed-comment ${comment.pending ? "is-pending" : ""}`}>
            <Avatar name={comment.authorName} src={comment.authorAvatarUrl ?? undefined} size={24} />
            <div className="feed-comment__body">
              <div className="feed-comment__header">
                <strong>{comment.authorName}</strong>
                <time dateTime={comment.createdAt}>{formatRelative(comment.createdAt)}</time>
              </div>
              <p><LinkedText text={comment.body} /></p>
            </div>
          </div>
        ))
      )}

      <div className="feed-comments__composer">
        <Textarea
          aria-label="Add a comment"
          placeholder="Write a reply…"
          value={value}
          maxRows={6}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <IconButton icon={<Icon name="send" size={14} />} label="Post comment" variant="solid" onClick={submit} disabled={!value.trim()} />
      </div>
    </div>
  );
}

/* ──────────────────────────────────── post card ──────────────────────────────────── */

interface PostCardProps {
  post: Pending<Post>;
  canModerate: boolean;
  isAuthor: boolean;
  expanded: boolean;
  comments: Pending<PostComment>[] | undefined;
  commentsLoading: boolean;
  onToggleReaction: (postId: string, emoji: string) => void;
  onToggleComments: (postId: string) => void;
  onSendComment: (postId: string, body: string) => void;
  onTogglePin: (post: Post) => void;
  onDelete: (post: Post) => void;
  onEdit: (post: Post) => void;
  onAcknowledge: (postId: string) => void;
  onShowAcks: (postId: string) => void;
}

function PostCard({
  post,
  canModerate,
  isAuthor,
  expanded,
  comments,
  commentsLoading,
  onToggleReaction,
  onToggleComments,
  onSendComment,
  onTogglePin,
  onDelete,
  onEdit,
  onAcknowledge,
  onShowAcks,
}: PostCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const canDelete = isAuthor || canModerate;
  const canEdit = isAuthor || canModerate;
  const teams = post.teams ?? [];
  const images = post.images ?? [];
  const showAckProgress = post.requiresAck && post.ackCount != null && post.audienceCount != null;

  return (
    <article
      id={`post-${post.id}`}
      className={`feed-post ${post.pinnedAt ? "is-pinned" : ""} ${post.pending ? "is-pending" : ""}`}
    >
      {post.pinnedAt && (
        <div className="feed-post__pin-banner">
          <Icon name="pin" size={12} />
          Pinned
        </div>
      )}

      <div className="feed-post__header">
        <Avatar name={post.authorName} src={post.authorAvatarUrl ?? undefined} size={36} />
        <div className="feed-post__author">
          <strong>{post.authorName}</strong>
          <time dateTime={post.createdAt}>{formatRelative(post.createdAt)}</time>
          {post.editedAt && <span className="feed-post__edited" title={new Date(post.editedAt).toLocaleString()}>edited</span>}
        </div>

        {post.kind !== "update" && (
          <Pill tone={post.kind === "announcement" ? "accent" : post.kind === "celebration" ? "ok" : "info"}>{post.kind}</Pill>
        )}

        <div className="feed-post__spacer" />

        {(canDelete || canModerate || canEdit) && (
          <Menu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            items={[
              ...(canModerate
                ? [
                    {
                      id: "pin",
                      label: post.pinnedAt ? "Unpin" : "Pin to top",
                      icon: "pin" as const,
                      onSelect: () => onTogglePin(post),
                    },
                  ]
                : []),
              ...(canEdit
                ? [
                    {
                      id: "edit",
                      label: "Edit",
                      icon: "edit" as const,
                      onSelect: () => onEdit(post),
                    },
                  ]
                : []),
              ...(canDelete
                ? [
                    {
                      id: "delete",
                      label: "Delete",
                      icon: "trash" as const,
                      destructive: true,
                      onSelect: () => onDelete(post),
                    },
                  ]
                : []),
            ]}
          >
            <IconButton
              icon={<Icon name="more" size={15} />}
              label="More actions"
              variant={menuOpen ? "soft" : "ghost"}
              onClick={() => setMenuOpen((v) => !v)}
            />
          </Menu>
        )}
      </div>

      {teams.length > 0 && (
        <div className="feed-post__audience">
          <Icon name="people" size={12} />
          {teams.map((t) => (
            <span key={t.id} className="feed-team-chip is-static">{t.name}</span>
          ))}
        </div>
      )}

      <p className="feed-post__body"><LinkedText text={post.body} /></p>

      {images.length > 0 && (
        <div className="feed-post__images">
          {images.map((image) => (
            <PostImage key={image.id} image={image} />
          ))}
        </div>
      )}

      {post.requiresAck && (
        <div className={`feed-post__ack ${post.acknowledged || isAuthor ? "is-done" : ""}`}>
          {isAuthor ? (
            <span>You posted this announcement.</span>
          ) : post.acknowledged ? (
            <span><Icon name="check" size={13} /> You acknowledged this.</span>
          ) : (
            <Button size="sm" onClick={() => onAcknowledge(post.id)}>
              Acknowledge
            </Button>
          )}
          {showAckProgress && (
            <button type="button" className="feed-post__ack-progress" onClick={() => onShowAcks(post.id)}>
              {post.ackCount} of {post.audienceCount} acknowledged
            </button>
          )}
        </div>
      )}

      <div className="feed-post__reactions">
        {post.reactions.map((reaction) => (
          <button
            key={reaction.emoji}
            type="button"
            className={`feed-reaction-chip ${reaction.reacted ? "is-reacted" : ""}`}
            onClick={() => onToggleReaction(post.id, reaction.emoji)}
            aria-pressed={reaction.reacted}
          >
            <span aria-hidden="true">{reaction.emoji}</span>
            <span>{reaction.count}</span>
          </button>
        ))}

        <div className="feed-post__quick-reactions">
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="feed-reaction-add"
              onClick={() => onToggleReaction(post.id, emoji)}
              aria-label={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>

        <div className="feed-post__spacer" />

        <button type="button" className="feed-post__comment-toggle" onClick={() => onToggleComments(post.id)}>
          <Icon name="chat" size={14} />
          {post.commentCount > 0 ? `${post.commentCount} ${post.commentCount === 1 ? "comment" : "comments"}` : "Comment"}
        </button>
      </div>

      {expanded && (
        <CommentThread postId={post.id} comments={comments} loading={commentsLoading} onSend={onSendComment} />
      )}
    </article>
  );
}

/* ──────────────────────────────────── edit modal ──────────────────────────────────── */

function EditPostModal({
  post,
  teams,
  onClose,
  onSave,
}: {
  post: Post | null;
  teams: TeamOption[];
  onClose: () => void;
  onSave: (post: Post, input: { body: string; teamIds: string[]; imageIds: string[] }) => Promise<void>;
}) {
  const [body, setBody] = useState("");
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [images, setImages] = useState<PostImageRef[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!post) return;
    setBody(post.body);
    setTeamIds((post.teams ?? []).map((t) => t.id));
    setImages(post.images ?? []);
  }, [post]);

  return (
    <Modal open={!!post} onClose={onClose} title="Edit post">
      <Textarea aria-label="Post text" value={body} maxRows={12} onChange={(e) => setBody(e.target.value)} />
      {images.length > 0 && (
        <div className="feed-composer__images">
          {images.map((image) => (
            <div key={image.id} className="feed-composer__image">
              <PostImage image={image} />
              <IconButton
                icon={<Icon name="close" size={12} />}
                label={`Remove ${image.fileName}`}
                variant="solid"
                onClick={() => setImages((prev) => prev.filter((i) => i.id !== image.id))}
              />
            </div>
          ))}
        </div>
      )}
      <TeamPicker teams={teams} value={teamIds} onChange={setTeamIds} />
      <div className="feed-delete-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          loading={saving}
          disabled={!body.trim()}
          onClick={() => {
            if (!post) return;
            setSaving(true);
            void onSave(post, { body: body.trim(), teamIds, imageIds: images.map((i) => i.id) }).finally(() => setSaving(false));
          }}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
}

/* ──────────────────────────────────── main view ──────────────────────────────────── */

export function Feed() {
  usePageTitle("Feed");
  const { viewer } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState<Pending<Post>[]>([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [deleting, setDeleting] = useState<Pending<Post> | null>(null);

  const [expandedPostId, setExpandedPostId] = useState<string | undefined>(undefined);
  const [commentsByPost, setCommentsByPost] = useState<Map<string, Pending<PostComment>[]>>(new Map());
  const [commentsLoadingId, setCommentsLoadingId] = useState<string | undefined>(undefined);

  const { active } = useWorkspace();
  // The office role decides, not the account-wide one: someone can run one
  // office and be an ordinary member of another.
  const officeRole = active?.role ?? viewer?.role;
  const canModerate = officeRole === "admin" || officeRole === "manager";
  const canAnnounce = canModerate;

  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [pendingAcks, setPendingAcks] = useState<Pending<Post>[]>([]);
  const [pendingAckTotal, setPendingAckTotal] = useState(0);
  const [editing, setEditing] = useState<Post | null>(null);
  const [acksFor, setAcksFor] = useState<string | null>(null);

  const loadPendingAcks = useCallback(() => {
    api
      .get<{ posts: Post[]; total: number }>("/posts/announcements/pending?limit=3")
      .then((res) => {
        setPendingAcks(res.posts);
        setPendingAckTotal(res.total);
      })
      .catch(() => {
        // The banner is a nudge, not the feed: failing quietly beats a toast on every visit.
      });
  }, []);

  useEffect(() => {
    api
      .get<{ teams: TeamOption[] }>("/teams?limit=100")
      .then((res) => setTeams(res.teams))
      .catch(() => setTeams([]));
    loadPendingAcks();
  }, [loadPendingAcks]);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ posts: Post[]; hasMore: boolean; nextCursor?: string }>("/posts?limit=20")
      .then((res) => {
        if (cancelled) return;
        setPosts(res.posts);
        setHasMore(res.hasMore);
        setNextCursor(res.nextCursor);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load the feed.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => load(), [load]);

  /*
   * Live posts and comments from everyone else.
   *
   * Own posts/comments are already in state via the optimistic-then-confirmed
   * flow in handlePost/sendComment below — appending them again here on the
   * frame this same action just triggered would double them, so both cases
   * are filtered on authorId. Feed has no membership list to scope a
   * narrower subscription by, so this rides the same workspace-wide topic
   * `channel_new` uses (see WORKSPACE_TOPIC in the gateway).
   */
  useRealtimeFrames((frame) => {
    if (frame.t === "post") {
      if (frame.post.authorId === viewer?.id) return;
      setPosts((prev) => (prev.some((p) => p.id === frame.post.id) ? prev : [frame.post, ...prev]));
      if (frame.post.requiresAck) loadPendingAcks();
      return;
    }
    if (frame.t === "post_updated") {
      // Keep what is specific to this viewer: the frame is built for no one in
      // particular, so its acknowledged flag, reactions and comment count would
      // otherwise overwrite their own.
      setPosts((prev) =>
        prev.map((p) =>
          p.id === frame.post.id
            ? {
                ...p,
                ...frame.post,
                acknowledged: p.acknowledged,
                reactions: p.reactions,
                commentCount: p.commentCount,
              }
            : p,
        ),
      );
      return;
    }
    if (frame.t === "post_comment") {
      if (frame.comment.authorId === viewer?.id) return;
      setPosts((prev) =>
        prev.map((p) => (p.id === frame.postId ? { ...p, commentCount: p.commentCount + 1 } : p)),
      );
      // Only append into the thread itself if it's open — otherwise the
      // bumped commentCount above is all there is to update, the same way a
      // Chat channel that isn't open just gets its unread badge bumped.
      if (frame.postId !== expandedPostId) return;
      setCommentsByPost((prev) => {
        const existing = prev.get(frame.postId) ?? [];
        if (existing.some((c) => c.id === frame.comment.id)) return prev;
        const next = new Map(prev);
        next.set(frame.postId, [...existing, frame.comment]);
        return next;
      });
    }
  });

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    api
      .get<{ posts: Post[]; hasMore: boolean; nextCursor?: string }>(`/posts?limit=20&cursor=${nextCursor}`)
      .then((res) => {
        // The cursor page can legitimately overlap the pinned posts already
        // rendered at the top, since pinned posts bypass pagination entirely.
        setPosts((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...res.posts.filter((p) => !seen.has(p.id))];
        });
        setHasMore(res.hasMore);
        setNextCursor(res.nextCursor);
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not load more posts.", { tone: "danger" });
      })
      .finally(() => setLoadingMore(false));
  }, [nextCursor, loadingMore, toast]);

  /* ── infinite scroll: a sentinel at the bottom of the list triggers loadMore ── */
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const handlePost = useCallback(
    (input: PostInput) => {
      if (!viewer) return;
      setPosting(true);

      const clientId = newClientId();
      const optimistic: Pending<Post> = {
        id: clientId,
        authorId: viewer.id,
        authorName: viewer.displayName,
        authorAvatarUrl: null,
        body: input.body,
        kind: input.kind,
        pinnedAt: null,
        createdAt: new Date().toISOString(),
        commentCount: 0,
        reactions: [],
        teams: teams.filter((t) => input.teamIds.includes(t.id)).map((t) => ({ id: t.id, name: t.name })),
        requiresAck: input.kind === "announcement",
        pending: true,
      };
      setPosts((prev) => [optimistic, ...prev]);

      api
        .post<{ post: Post }>("/posts", input)
        .then((res) => {
          setPosts((prev) => prev.map((p) => (p.id === clientId ? { ...res.post } : p)));
        })
        .catch((error: unknown) => {
          setPosts((prev) => prev.filter((p) => p.id !== clientId));
          toast(error instanceof ApiError ? error.message : "Could not post that update.", { tone: "danger" });
        })
        .finally(() => setPosting(false));
    },
    [viewer, toast, teams],
  );

  const acknowledge = useCallback(
    (postId: string) => {
      api
        .post<{ post: Post }>(`/posts/${postId}/acknowledge`)
        .then((res) => {
          setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, ...res.post } : p)));
          setPendingAcks((prev) => prev.filter((p) => p.id !== postId));
          setPendingAckTotal((n) => Math.max(0, n - 1));
          loadPendingAcks();
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not record your acknowledgement.", { tone: "danger" });
        });
    },
    [toast, loadPendingAcks],
  );

  const saveEdit = useCallback(
    (post: Post, input: { body: string; teamIds: string[]; imageIds: string[] }) => {
      return api
        .patch<{ post: Post }>(`/posts/${post.id}`, { ...input, version: post.version })
        .then((res) => {
          setPosts((prev) =>
            prev.map((p) =>
              p.id === post.id
                ? { ...p, ...res.post, acknowledged: p.acknowledged, reactions: p.reactions, commentCount: p.commentCount }
                : p,
            ),
          );
          setEditing(null);
        })
        .catch((error: unknown) => {
          const stale = error instanceof ApiError && error.status === 409;
          toast(
            stale
              ? "Someone else changed this post. Reload the feed and try again."
              : error instanceof ApiError
                ? error.message
                : "Could not save that edit.",
            { tone: "danger" },
          );
        });
    },
    [toast],
  );

  const toggleReaction = useCallback(
    (postId: string, emoji: string) => {
      if (!viewer) return;

      // Optimistic toggle, reconciled with the server's grouped counts once
      // the request resolves — same pattern as chat message reactions.
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id !== postId) return p;
          const existing = p.reactions.find((r) => r.emoji === emoji);
          const nextReactions = existing
            ? p.reactions
                .map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, reacted: false } : r))
                .filter((r) => r.count > 0)
            : [...p.reactions, { emoji, count: 1, reacted: true }];
          return { ...p, reactions: nextReactions };
        }),
      );

      api
        .post<{ reactions: Reaction[] }>(`/posts/${postId}/reactions`, { emoji })
        .then((res) => {
          setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, reactions: res.reactions } : p)));
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not update reaction.", { tone: "danger" });
        });
    },
    [viewer, toast],
  );

  const toggleComments = useCallback(
    (postId: string) => {
      const next = expandedPostId === postId ? undefined : postId;
      setExpandedPostId(next);
      if (!next || commentsByPost.has(next)) return;

      setCommentsLoadingId(next);
      api
        .get<{ post: Post; comments: PostComment[] }>(`/posts/${next}`)
        .then((res) => {
          setCommentsByPost((prev) => new Map(prev).set(next, res.comments));
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not load comments.", { tone: "danger" });
        })
        .finally(() => setCommentsLoadingId(undefined));
    },
    [expandedPostId, commentsByPost, toast],
  );

  /*
   * Focus a single post named by `?post=` — the destination a notification
   * deep-links to (a mention in a post's comments, e.g.).
   *
   * The feed loads a page at a time, so the target post may simply not be
   * loaded yet. Walk `loadMore` forward (capped, mirroring Chat.tsx's
   * pagination-walk for `?message=`) until it turns up or the feed runs out
   * of pages, then scroll it into view and expand its comments. Give up
   * gracefully — toast, matching the tone of Chat's "further back than this
   * thread has loaded" message — rather than leaving the effect spinning.
   */
  useEffect(() => {
    const postId = searchParams.get("post");
    if (!postId || loading) return;

    const found = posts.some((p) => p.id === postId);
    if (found) {
      document.getElementById(`post-${postId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setExpandedPostId((current) => current ?? postId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("post");
          return next;
        },
        { replace: true },
      );
      return;
    }

    // Not loaded yet: try the next page. Once `hasMore` is false or we've
    // tried a handful of pages, the post is either deleted or otherwise
    // unreachable — stop rather than loop forever.
    const MAX_PAGES = 5;
    let cancelled = false;
    let attempts = 0;

    const tryNext = () => {
      if (cancelled) return;
      if (!hasMore || attempts >= MAX_PAGES) {
        toast("That post could not be found.", { tone: "danger" });
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("post");
            return next;
          },
          { replace: true },
        );
        return;
      }
      attempts += 1;
      loadMore();
    };

    if (!loadingMore) tryNext();

    return () => {
      cancelled = true;
    };
    // Re-runs as `posts`/`hasMore`/`loadingMore` change so each successive
    // page load gets checked in turn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, posts, loading, hasMore, loadingMore]);

  const sendComment = useCallback(
    (postId: string, body: string) => {
      if (!viewer) return;
      const clientId = newClientId();
      const optimistic: Pending<PostComment> = {
        id: clientId,
        body,
        authorId: viewer.id,
        authorName: viewer.displayName,
        authorAvatarUrl: null,
        createdAt: new Date().toISOString(),
        pending: true,
      };
      setCommentsByPost((prev) => {
        const next = new Map(prev);
        next.set(postId, [...(next.get(postId) ?? []), optimistic]);
        return next;
      });
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p)));

      api
        .post<{ comment: PostComment }>(`/posts/${postId}/comments`, { body })
        .then((res) => {
          setCommentsByPost((prev) => {
            const next = new Map(prev);
            next.set(postId, (next.get(postId) ?? []).map((c) => (c.id === clientId ? { ...res.comment } : c)));
            return next;
          });
        })
        .catch((error: unknown) => {
          setCommentsByPost((prev) => {
            const next = new Map(prev);
            next.set(postId, (next.get(postId) ?? []).filter((c) => c.id !== clientId));
            return next;
          });
          setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, commentCount: Math.max(0, p.commentCount - 1) } : p)));
          toast(error instanceof ApiError ? error.message : "Comment failed to send.", { tone: "danger" });
        });
    },
    [viewer, toast],
  );

  const togglePin = useCallback(
    (post: Post) => {
      const previousPosts = posts;
      const nextPinnedAt = post.pinnedAt ? null : new Date().toISOString();
      // Optimistically re-sort so a newly-pinned post jumps to the top with
      // the rest of the pinned set, matching what the server will return.
      setPosts((prev) => {
        const updated = prev.map((p) => (p.id === post.id ? { ...p, pinnedAt: nextPinnedAt } : p));
        const pinned = updated.filter((p) => p.pinnedAt).sort((a, b) => (a.pinnedAt! < b.pinnedAt! ? 1 : -1));
        const rest = updated.filter((p) => !p.pinnedAt);
        return [...pinned, ...rest];
      });

      api
        .post<{ post: Post }>(`/posts/${post.id}/pin`)
        .then((res) => {
          setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, ...res.post } : p)));
        })
        .catch((error: unknown) => {
          setPosts(previousPosts);
          toast(error instanceof ApiError ? error.message : "Could not update pin state.", { tone: "danger" });
        });
    },
    [posts, toast],
  );

  const confirmDelete = useCallback(() => {
    if (!deleting) return;
    const post = deleting;
    const previousPosts = posts;
    setPosts((prev) => prev.filter((p) => p.id !== post.id));
    setDeleting(null);

    api
      .delete(`/posts/${post.id}`)
      .then(() => {
        toast("Post deleted.", { tone: "ok" });
      })
      .catch((error: unknown) => {
        setPosts(previousPosts);
        toast(error instanceof ApiError ? error.message : "Could not delete that post.", { tone: "danger" });
      });
  }, [deleting, posts, toast]);

  return (
    <div className="feed-shell">
      <section className="feed-main" aria-label="Company feed">
        <PageHeader title="Feed" />

        <div className="feed-content">
          {pendingAckTotal > 0 && (
            <section className="feed-ack-banner" aria-label="Announcements waiting for you">
              <h2>
                <Icon name="pin" size={14} />
                {pendingAckTotal === 1 ? "1 announcement needs your acknowledgement" : `${pendingAckTotal} announcements need your acknowledgement`}
              </h2>
              {pendingAcks.map((post) => (
                <div key={post.id} className="feed-ack-banner__item">
                  <div>
                    <strong>{post.authorName}</strong>
                    <p>{post.body.length > 140 ? `${post.body.slice(0, 140)}…` : post.body}</p>
                  </div>
                  <Button size="sm" onClick={() => acknowledge(post.id)}>
                    Acknowledge
                  </Button>
                </div>
              ))}
            </section>
          )}

          <Composer onPost={handlePost} posting={posting} teams={teams} canAnnounce={canAnnounce} />

          {loading ? (
            <div className="feed-loading">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="feed-post">
                  <div className="feed-post__header">
                    <Skeleton circle width={36} height={36} />
                    <SkeletonText lines={1} />
                  </div>
                  <SkeletonText lines={3} />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <EmptyState icon="chat" title="No posts yet" description="Be the first to share an update with the company." />
          ) : (
            <div className="feed-list">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  canModerate={!!canModerate}
                  isAuthor={post.authorId === viewer?.id}
                  expanded={expandedPostId === post.id}
                  comments={commentsByPost.get(post.id)}
                  commentsLoading={commentsLoadingId === post.id}
                  onToggleReaction={toggleReaction}
                  onToggleComments={toggleComments}
                  onSendComment={sendComment}
                  onTogglePin={togglePin}
                  onDelete={setDeleting}
                  onEdit={setEditing}
                  onAcknowledge={acknowledge}
                  onShowAcks={setAcksFor}
                />
              ))}

              {hasMore && (
                <div className="feed-load-more" ref={sentinelRef}>
                  <Button variant="secondary" size="sm" loading={loadingMore} onClick={loadMore}>
                    Load more
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <EditPostModal post={editing} teams={teams} onClose={() => setEditing(null)} onSave={saveEdit} />
      <AckListModal postId={acksFor} onClose={() => setAcksFor(null)} />

      <Modal open={!!deleting} onClose={() => setDeleting(null)} title="Delete this post?">
        <p className="feed-delete-copy">
          This will be removed for everyone who could see it. This cannot be undone.
        </p>
        <div className="feed-delete-actions">
          <Button variant="ghost" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete post
          </Button>
        </div>
      </Modal>
    </div>
  );
}
